import { useQuery } from '@apollo/client'
import React, { useMemo } from 'react'

import { Dialog, LoadingSpinner } from '@syconium/little-miss-figgy/dist/components'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { FETCH_COLOR_GALLERY } from '../../../../lib/graphql/queries/FETCH_COLOR_GALLERY'

import GalleryTile from './ColorGalleryTile'
import * as Styles from './styles'

type ColorGalleryModalProps = {
  productGroupHandle: string
  colorCount: number
}

const ColorGalleryModal = ({ productGroupHandle, colorCount }: ColorGalleryModalProps) => {
  const {
    colorGallery: { colors, title },
  } = useTranslation()

  const { data, loading } = useQuery(FETCH_COLOR_GALLERY, {
    skip: !productGroupHandle,
    variables: { productGroupHandle },
    onError: error =>
      reportClientError({
        error,
        context: { scope: 'Color Gallery Modal' },
      }),
  })

  const productGroup = data?.productGroup

  // Merge colorGroup data with product data
  const colorGroups = useMemo(() => {
    if (!productGroup) return []

    const { products = [], colorGroups = [] } = productGroup

    return colorGroups.map(group => ({
      ...group,
      colors: group.colors.map(color => {
        const matchingProduct = products.find(p => p.colorInfo?.rawName === color.rawName)
        return {
          ...color,
          name: matchingProduct?.colorInfo?.name || '',
          imageUrl: matchingProduct?.images?.nodes?.[0]?.source || null,
          handle: matchingProduct?.handle || '',
        }
      }),
    }))
  }, [productGroup])

  const ModalContent = () => {
    return loading ? (
      <Styles.LoadingSpinnerWrapper>
        <LoadingSpinner label='Loading colors…' size='100px' />
      </Styles.LoadingSpinnerWrapper>
    ) : (
      <Styles.DialogContent>
        {colorGroups.map(group => (
          <React.Fragment key={group.label}>
            <Styles.DialogTitle>
              {group.label} {colors}
            </Styles.DialogTitle>
            <Styles.GalleryWrap>
              {group.colors.map(color => {
                if (!color.imageUrl) return null
                return (
                  <GalleryTile
                    key={color.rawName || color.name}
                    colorDisplayValue={color.name}
                    colorKey={color.rawName}
                    productGroupHandle={productGroupHandle}
                    imageUrl={color.imageUrl}
                  />
                )
              })}
            </Styles.GalleryWrap>
          </React.Fragment>
        ))}
      </Styles.DialogContent>
    )
  }

  return (
    <Styles.DialogModal
      fullscreenMobile
      header={
        <Dialog.Header
          title={
            <Styles.ModalTitleWrap>
              <Dialog.Title align='left'>
                <Styles.DialogHeader>{title}</Styles.DialogHeader>
              </Dialog.Title>
              {colorCount && (
                <Styles.ColorCount>
                  {colorCount} {colors}
                </Styles.ColorCount>
              )}
            </Styles.ModalTitleWrap>
          }
          dismiss={<Dialog.Dismiss aria-label='Close modal' />}
        />
      }
      footer={null}
      content={<ModalContent />}
    ></Styles.DialogModal>
  )
}

export default ColorGalleryModal
